"use client";

import React, { useState, useEffect } from "react";
import { Settings, ChevronDown, ChevronUp, Eye, EyeOff, Save, Loader2, Check } from "lucide-react";
import { Disclosure, Transition, Listbox } from '@headlessui/react';
import { Project, DEFAULT_PROJECT_CONFIG } from "@/types/project.interface";
import type { PluginMeta } from "@/lib/processors/base";

interface ProjectPanelProps {
    project: Project | null;
    onSave: (data: Partial<Project>) => Promise<void>;
    onCreateProject: (data: Omit<Project, "id" | "createdAt" | "updatedAt">) => Promise<void>;
}

export const ProjectPanel: React.FC<ProjectPanelProps> = ({ project, onSave, onCreateProject }) => {
    const [showApiKey, setShowApiKey] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [plugins, setPlugins] = useState<PluginMeta[]>([]);
    const [pluginsLoading, setPluginsLoading] = useState(true);

    // 从 /api/processors 动态加载插件列表
    useEffect(() => {
        fetch("/api/processors")
            .then(r => r.json())
            .then((data: PluginMeta[]) => {
                if (Array.isArray(data)) setPlugins(data);
            })
            .catch(e => console.error("[ProjectPanel] 加载插件列表失败:", e))
            .finally(() => setPluginsLoading(false));
    }, []);

    const [form, setForm] = useState<Omit<Project, "id" | "createdAt" | "updatedAt">>({
        name: project?.name ?? "新项目",
        apiKey: project?.apiKey ?? "",
        order: project?.order ?? DEFAULT_PROJECT_CONFIG.order,
        googleProjectId: project?.googleProjectId ?? "",
        googleLocation: project?.googleLocation ?? "us-central1",
    });

    // 当 project 变化时同步表单
    React.useEffect(() => {
        if (project) {
            setForm({
                name: project.name,
                apiKey: project.apiKey,
                order: project.order,
                googleProjectId: project.googleProjectId ?? "",
                googleLocation: project.googleLocation ?? "us-central1",
            });
        } else {
            setForm({
                name: "新项目",
                apiKey: "",
                order: DEFAULT_PROJECT_CONFIG.order,
                googleProjectId: "",
                googleLocation: "us-central1",
            });
        }
    }, [project?.id]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (project) {
                await onSave(form);
            } else {
                await onCreateProject(form);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Disclosure defaultOpen={true}>
            {({ open }) => (
                <div className="mb-6 bg-zinc-50 dark:bg-zinc-900/70 border border-zinc-200 dark:border-zinc-700 rounded-none overflow-hidden">
                    {/* 折叠标题栏 */}
                    <Disclosure.Button className="w-full flex justify-between items-center p-4 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 transition-colors">
                        <div className="flex items-center gap-2">
                            <Settings size={16} className="text-blue-500 dark:text-blue-400" />
                            <span className="font-semibold text-sm text-zinc-800 dark:text-zinc-200">
                                项目配置 {project ? `— ${form.name}` : "— 未创建"}
                            </span>
                            {!project?.apiKey && (
                                <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 rounded-none">缺少 API Key</span>
                            )}
                        </div>
                        <ChevronUp size={16} className={`text-zinc-400 transition-transform ${open ? '' : 'rotate-180'}`} />
                    </Disclosure.Button>

                    <Transition
                        enter="transition duration-200 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-100 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Disclosure.Panel className="p-4 pt-0 space-y-4 border-t border-zinc-200 dark:border-zinc-800">
                            {/* 项目名称 */}
                            <div className="mt-4">
                                <label className="text-xs text-zinc-500 dark:text-zinc-400 block mb-1">项目名称</label>
                                <input
                                    className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:border-blue-500 transition-colors placeholder:text-zinc-400"
                                    value={form.name}
                                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="我的视频项目"
                                />
                            </div>

                            {/* Google Cloud Configuration (Always Vertex AI) */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-zinc-500 dark:text-zinc-400 block mb-1">
                                        Google Project ID
                                        <span className="ml-1 text-[10px] opacity-70">(e.g. my-project-123)</span>
                                    </label>
                                    <input
                                        className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:border-blue-500 transition-colors"
                                        value={form.googleProjectId ?? ""}
                                        onChange={e => setForm(prev => ({ ...prev, googleProjectId: e.target.value }))}
                                        placeholder="GCP Project ID"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-zinc-500 dark:text-zinc-400 block mb-1">
                                        Google Location
                                    </label>
                                    <input
                                        className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:border-blue-500 transition-colors"
                                        value={form.googleLocation ?? ""}
                                        onChange={e => setForm(prev => ({ ...prev, googleLocation: e.target.value }))}
                                        placeholder="us-central1"
                                    />
                                </div>
                            </div>

                            {/* API Key */}
                            <div>
                                <label className="text-xs text-zinc-500 dark:text-zinc-400 block mb-1">Google API Key</label>
                                <div className="relative">
                                    <input
                                        type={showApiKey ? "text" : "password"}
                                        className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-3 py-2 pr-10 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:border-blue-500 transition-colors font-mono placeholder:text-zinc-400"
                                        value={form.apiKey}
                                        onChange={e => setForm(prev => ({ ...prev, apiKey: e.target.value }))}
                                        placeholder="输入您的 API Key..."
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowApiKey(!showApiKey)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                                    >
                                        {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            {/* 保存按钮 */}
                            <button
                                onClick={handleSave}
                                disabled={isSaving || !form.name}
                                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-200 dark:disabled:bg-zinc-700 disabled:text-zinc-400 py-2 rounded-none text-sm font-medium text-white transition-all"
                            >
                                {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                {project ? "保存基础配置" : "创建基础项目"}
                            </button>
                        </Disclosure.Panel>
                    </Transition>
                </div>
            )}
        </Disclosure>
    );
};
